// ============================================================
// pages/admin/AdminCampaigns.jsx — Campaign Management (Firebase)
// ============================================================

import React, { useState, useRef } from "react";
import { useCampaigns, useCustomers } from "../../hooks/useFirestore";
import { CampaignService } from "../../services/firebaseServices";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase.config";

export default function AdminCampaigns({ showToast }) {
  const { campaigns, loading, refresh } = useCampaigns();
  const { customers } = useCustomers();
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "", type: "whatsapp", subject: "", body: "",
    audience: "all", audienceTier: "gold", imageURL: "",
    selectedCustomerIds: [],
  });
  const [customerSearch, setCustomerSearch] = useState("");

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const toggleCustomer = (uid) => {
    setForm((prev) => {
      const ids = prev.selectedCustomerIds || [];
      return {
        ...prev,
        selectedCustomerIds: ids.includes(uid)
          ? ids.filter((id) => id !== uid)
          : [...ids, uid],
      };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      showToast("Please fill in campaign name and message", "error");
      return;
    }
    try {
      await CampaignService.create({
        name: form.name,
        type: form.type,
        subject: form.type === "email" ? form.subject : null,
        body: form.body,
        audience: form.audience,
        audienceTier: form.audience === "tier" ? form.audienceTier : null,
        audienceCustomerIds: form.audience === "custom" ? form.selectedCustomerIds : [],
        estimatedReach: 0,
        scheduledAt: null,
        imageURL: form.imageURL || null,
      });
      showToast("Campaign created!", "success");
      setCreating(false);
      setForm({ name: "", type: "whatsapp", subject: "", body: "", audience: "all", audienceTier: "gold", imageURL: "", selectedCustomerIds: [] });
      setCustomerSearch("");
      refresh();
    } catch (err) {
      showToast("Failed to create campaign", "error");
    }
  };

  const handleSend = async (campaignId) => {
    if (!window.confirm("Send this campaign now? This cannot be undone.")) return;
    setSending(campaignId);
    try {
      const result = await CampaignService.send(campaignId);
      showToast(`Campaign sent to ${result.totalSent} customers!`, "success");
      refresh();
    } catch (err) {
      showToast("Failed to send campaign", "error");
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (campaignId, name) => {
    if (!window.confirm(`Delete campaign "${name}"? This cannot be undone.`)) return;
    try {
      await CampaignService.delete(campaignId);
      showToast("Campaign deleted", "success");
      refresh();
    } catch (err) {
      showToast("Failed to delete campaign", "error");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const statusColors = {
    draft: { bg: "#FFF3CD", color: "#856404" },
    scheduled: { bg: "#CCE5FF", color: "#004085" },
    sent: { bg: "#D4EDDA", color: "#155724" },
    failed: { bg: "#F8D7DA", color: "#721C24" },
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Marketing Campaigns</h2>
          <p>Create and send WhatsApp, email, and push campaigns</p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(!creating)}>
          {creating ? "Cancel" : "+ New Campaign"}
        </button>
      </div>

      {/* Create Campaign Form */}
      {creating && (
        <div className="card" style={{ marginBottom: "2rem", padding: "1.5rem" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            New Campaign
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div className="form-group">
              <label className="form-label">Campaign Name</label>
              <input className="form-input" value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Eid Special Promo" />
            </div>
            <div className="form-group">
              <label className="form-label">Channel</label>
              <select className="form-select" value={form.type}
                onChange={(e) => update("type", e.target.value)}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="push">Push Notification</option>
              </select>
            </div>
          </div>

          {form.type === "email" && (
            <div className="form-group">
              <label className="form-label">Email Subject</label>
              <input className="form-input" value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                placeholder="Email subject line" />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea className="form-textarea" rows="4" value={form.body}
              onChange={(e) => update("body", e.target.value)}
              placeholder="Write your campaign message..." />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div className="form-group">
              <label className="form-label">Audience</label>
              <select className="form-select" value={form.audience}
                onChange={(e) => update("audience", e.target.value)}>
                <option value="all">All Customers</option>
                <option value="tier">Specific Tier</option>
                <option value="custom">Custom List</option>
              </select>
            </div>
            {form.audience === "tier" && (
              <div className="form-group">
                <label className="form-label">Target Tier</label>
                <select className="form-select" value={form.audienceTier}
                  onChange={(e) => update("audienceTier", e.target.value)}>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </select>
              </div>
            )}
          </div>

          {/* Custom customer picker */}
          {form.audience === "custom" && (
            <div className="form-group">
              <label className="form-label">
                Select Customers ({form.selectedCustomerIds.length} selected)
              </label>
              <input className="form-input" placeholder="Search customers..."
                value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)}
                style={{ marginBottom: "0.5rem" }} />
              <div style={{
                maxHeight: 200, overflowY: "auto", border: "1px solid rgba(196,136,58,0.15)",
                borderRadius: 8, padding: "0.25rem",
              }}>
                {customers
                  .filter((c) => c.role === "customer")
                  .filter((c) => (c.displayName || c.email || "").toLowerCase().includes(customerSearch.toLowerCase()))
                  .map((c) => (
                    <label key={c.id} style={{
                      display: "flex", alignItems: "center", gap: "0.5rem",
                      padding: "0.5rem 0.75rem", cursor: "pointer", borderRadius: 6,
                      background: (form.selectedCustomerIds || []).includes(c.id) ? "rgba(196,136,58,0.08)" : "transparent",
                    }}>
                      <input type="checkbox"
                        checked={(form.selectedCustomerIds || []).includes(c.id)}
                        onChange={() => toggleCustomer(c.id)}
                        style={{ width: 16, height: 16 }} />
                      <div>
                        <div style={{ fontSize: "0.88rem", fontWeight: 600 }}>{c.displayName || "No name"}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--cocoa)" }}>{c.email}</div>
                      </div>
                      <span className="inline-tag" style={{ marginLeft: "auto", textTransform: "capitalize" }}>
                        {c.loyaltyTier || "bronze"}
                      </span>
                    </label>
                  ))}
                {customers.filter((c) => c.role === "customer").length === 0 && (
                  <div style={{ padding: "1rem", textAlign: "center", color: "var(--cocoa)", fontSize: "0.85rem" }}>
                    No customers found
                  </div>
                )}
              </div>
              {form.selectedCustomerIds.length > 0 && (
                <button className="btn btn-outline btn-sm" style={{ marginTop: "0.5rem" }}
                  onClick={() => update("selectedCustomerIds", [])}>
                  Clear selection
                </button>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Promotional Image (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {form.imageURL && (
                <img src={form.imageURL} alt="Campaign" style={{
                  width: 100, height: 60, objectFit: "cover", borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.1)",
                }} />
              )}
              <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}
                disabled={uploading}>
                {uploading ? "Uploading..." : form.imageURL ? "Change Image" : "Upload Image"}
              </button>
              {form.imageURL && (
                <button className="btn btn-sm" style={{ background: "#F8D7DA", color: "#721C24" }}
                  onClick={() => update("imageURL", "")}>
                  Remove
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const storageRef = ref(storage, `campaigns/${Date.now()}_${file.name}`);
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    update("imageURL", url);
                    showToast("Image uploaded!", "success");
                  } catch (err) {
                    showToast("Failed to upload image", "error");
                  } finally {
                    setUploading(false);
                  }
                }} />
            </div>
          </div>

          <button className="btn btn-primary" onClick={handleCreate}>
            Create Campaign
          </button>
        </div>
      )}

      {/* Campaign List */}
      {loading ? (
        <div className="empty-state"><p>Loading campaigns...</p></div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <h3>No campaigns yet</h3>
          <p>Create your first marketing campaign to reach your customers.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Channel</th>
                <th>Audience</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const sc = statusColors[c.status] || statusColors.draft;
                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                        {c.imageURL && (
                          <img src={c.imageURL} alt="" style={{
                            width: 48, height: 48, borderRadius: 6, objectFit: "cover", flexShrink: 0,
                          }} />
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: "0.78rem", color: "var(--cocoa)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.body}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="inline-tag" style={{ textTransform: "capitalize" }}>
                        {c.type}
                      </span>
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {c.audience === "tier" ? `${c.audienceTier} tier` : c.audience}
                    </td>
                    <td>
                      <span style={{
                        display: "inline-block", padding: "0.2rem 0.7rem", borderRadius: 12,
                        fontSize: "0.75rem", fontWeight: 600, background: sc.bg, color: sc.color,
                      }}>
                        {c.status}
                      </span>
                    </td>
                    <td>
                      {c.totalSent || 0}
                      {c.sentAt && (
                        <div style={{ fontSize: "0.75rem", color: "var(--cocoa)" }}>
                          {formatDate(c.sentAt)}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{formatDate(c.createdAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        {c.status === "draft" && (
                          <button className="btn btn-primary btn-sm"
                            disabled={sending === c.id}
                            onClick={() => handleSend(c.id)}>
                            {sending === c.id ? "Sending..." : "Send Now"}
                          </button>
                        )}
                        {c.status === "sent" && (
                          <span style={{ fontSize: "0.82rem", color: "var(--sage-dark)" }}>✓ Sent</span>
                        )}
                        <button className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(c.id, c.name)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}